import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { TocList } from "discover.medical.react-component-library";
import ContentTocItem from "../ContentTocItem";
import { useStore } from "../../store";

// Disable the toc item if the following is true:
// it is preview mode
// the article is not free
// no section exists for the given section id
const isItemDisabled = (userExperience, contentSections, id) => {
  const { isPreviewMode, isFreeArticle } = userExperience;
  return (
    isPreviewMode && !isFreeArticle && !contentSections.some((o) => o.id === id)
  );
};

// recursive function returns true if this tocItem is active or contains an active child, otherwise returns false.
const checkIsChildActive = (activeItem, subTree = []) => {
  return subTree.some((item) => {
    const isChildActive = checkIsChildActive(activeItem, item.subSection || []);
    return item.reference === activeItem || isChildActive;
  });
};

const ContentToc = ({
  handleTocItemClick,
  toc,
  activeReference,
  dataTestid = "content-page-toc",
  loadedSection,
}) => {
  const { contentSections, userExperience } = useStore();
  const activeSectionRef = useRef();

  //Track the active (top level) section. Used by ContentTocItem to auto-close any open sections when navigating between top-level sections
  useEffect(() => {
    const activeSection = toc.find((item) => {
      return (
        item.reference === activeReference ||
        checkIsChildActive(activeReference, item.subSection)
      );
    });
    const activeSectionId = activeSection?.reference || activeSection?.title;
    if (activeSectionRef.current !== activeSectionId) {
      activeSectionRef.current = activeSectionId;
    }
  }, [activeReference, toc]);

  return (
    <>
      <TocList
        data-testid={dataTestid}
        className="content-page-toc"
        color={"gray800"}
        hasMargin={userExperience.maxTocDepth !== 1}
      >
        {toc.map((item) => (
          <ContentTocItem
            key={item.reference}
            content={item}
            handleTocItemClick={handleTocItemClick}
            breadcrumb={[item.title]}
            isDisabled={isItemDisabled(
              userExperience,
              contentSections,
              item.reference
            )}
            maxTocDepth={userExperience.maxTocDepth}
            loadedSection={loadedSection}
            activeSection={activeSectionRef.current}
            activeReference={activeReference}
          />
        ))}
      </TocList>
    </>
  );
};

ContentToc.defaultProps = { toc: [] };

ContentToc.propTypes = {
  toc: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      reference: PropTypes.string.isRequired,
      subSection: PropTypes.array,
    })
  ),
  handleTocItemClick: PropTypes.func,
};

ContentToc.displayName = "ContentToc";

export default ContentToc;
